import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import useTopPanel from 'hooks/useTopPanel';
import React, { memo, useEffect, useState } from 'react';
// import SearchInput from 'components/SearchInput';
// import ContractFiltersComponent from 'components/ui/filters/ContractFiltersComponent';
import ContractTable from 'components/ui/tables/ContractTable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import useMenu from 'hooks/useMenu';
import useSearch from 'hooks/useSearch';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import ContractFilterDrawer from 'components/dashboard/ContractFilterDrawer';
import ObjectFilter from 'components/ui/filters/items/ObjectFilter';
import SearchInputNavbar from 'components/SearchInputNavbar';

/** Bitta yig'ma karta qiymati: UZS/USD dan mavjudini ko'rsatadi. */
const TotalMoney = ({ uzs, usd, danger = false }) => (
  <div
    className={`flex flex-col leading-tight tabular-nums font-semibold text-[15px] ${
      danger && (uzs > 0 || usd > 0) ? 'text-danger-700' : 'text-ink'
    }`}
  >
    {(uzs > 0 || (!uzs && !usd)) && (
      <CurrencyFormat allowNegative value={uzs || 0} suffix=' UZS' />
    )}
    {usd > 0 && <CurrencyFormat allowNegative value={usd} suffix=' $' />}
  </div>
);

/** Ro'yxat filtrlariga mos yig'ma statistika paneli. */
const ContractTotals = ({ search, refetchFlag }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const queryStr = search?.toString() || '';

  const { data, refetch } = useQuery({
    queryKey: ['/dictionary/contractstotals', queryStr],
    queryFn: async () =>
      (await axiosPrivate.get(`/dictionary/contractstotals?${queryStr}`)).data
        .data,
    retry: false,
  });

  useEffect(() => {
    if (refetchFlag) refetch();
  }, [refetchFlag]);

  const items = [
    {
      label: t('contract.stats.totalCount'),
      value: (
        <span className='font-semibold text-[15px] text-ink tabular-nums'>
          {data?.count ?? '—'}
        </span>
      ),
    },
    {
      label: t('contract.stats.totalSum'),
      value: <TotalMoney uzs={data?.sum_uzs} usd={data?.sum_usd} />,
    },
    {
      label: t('contract.stats.totalStart'),
      value: <TotalMoney uzs={data?.start_uzs} usd={data?.start_usd} />,
    },
    {
      label: t('contract.stats.totalLeft'),
      value: <TotalMoney uzs={data?.left_uzs} usd={data?.left_usd} danger />,
    },
  ];

  return (
    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2'>
      {items.map((it, i) => (
        <div
          key={i}
          className='bg-white border border-line rounded-lg px-4 py-2.5 min-w-0'
        >
          <div className='text-[12px] text-muted truncate mb-0.5'>
            {it.label}
          </div>
          {it.value}
        </div>
      ))}
    </div>
  );
};

const Contract = () => {
  const { setComponent } = useTopPanel();
  const search = useSearch();
  const { hasChild } = useMenu();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const view = (code) => hasChild('CONTRACTS', code);

  useEffect(() => {
    setComponent(
      <div className='component-title flex gap-4 whitespace-nowrap items-center'>
        {t('contract.title')}
        <div className='w-[200px]'>
          <ObjectFilter allowClear />
        </div>
      </div>,
    );
  }, [i18n.language]);

  const [refetch, setRefetch] = useState(false);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Excel eksport — auth'li API orqali: hisobot serverda tayyorlanadi,
  // tayyor bo'lgach modal ochiladi va undagi unikal havola orqali yuklab olinadi.
  const axiosPrivate = useAxiosPrivate();
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // {link, filename} — tayyor hisobot; null bo'lsa modal yopiq
  const [exportReady, setExportReady] = useState(null);

  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // search — URLSearchParams (length xossasi yo'q!), toString() bilan tekshiramiz
      const qsStr = search?.toString();
      const qs = qsStr ? `?${qsStr}` : '';
      const res = await axiosPrivate.get(`/admin/excel/contracts${qs}`);
      const info = res?.data?.data;
      if (info?.link) {
        setExportReady({ link: info.link, filename: info.filename || 'contracts.xlsx' });
      }
    } finally {
      setExporting(false);
    }
  };

  // Modal ichidagi unikal havola orqali faylni yuklab olish (auth header bilan)
  const handleDownloadReady = async () => {
    if (!exportReady?.link || downloading) return;
    setDownloading(true);
    try {
      const blob = await axiosPrivate.get(exportReady.link, {
        responseType: 'blob',
      });
      const objUrl = URL.createObjectURL(blob.data);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = exportReady.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      setExportReady(null);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className='component-list-wrapper'>
      <div className='component-list-header mb-2'>
        <div className='header-actions-container pt-2 pb-2 flex flex-row items-center'>
          <div className='header-actions filter-box flex items-center'>
            <Button
              variant='base'
              startIcon={<i className='bi bi-filter' />}
              className='!mr-2'
              onClick={() => setExpanded((prev) => !prev)}
            >
              {t('common.button.filter')}
            </Button>
          </div>
          <div className='grow flex items-center gap-2'>
            <SearchInputNavbar inputKey='name' fullWidth />
            <div className='ml-auto flex items-center gap-4 px-2 text-[13px] text-muted whitespace-nowrap'>
              <div className='flex items-center gap-1.5'>
                <span className='w-2.5 h-2.5 rounded-full bg-success' />
                {t('contract.completed')}
              </div>
              <div className='flex items-center gap-1.5'>
                <span className='w-2.5 h-2.5 rounded-full bg-danger' />
                {t('contract.cancelled')}
              </div>
            </div>
          </div>
          <div className='header-actions action-buttons-box flex items-center justify-center ml-4'>
            <Button
              variant='base'
              onClick={() => {
                setRefetch(true);
              }}
              disable={`${refetch}`}
            >
              <i
                className={`bi bi-arrow-repeat ${
                  refetch ? 'animate-spin' : ''
                }`}
              />
            </Button>
            <Button
              variant='base'
              color='success'
              className='!mx-2'
              onClick={handleExportExcel}
              disabled={exporting}
            >
              <i
                className={`bi ${exporting ? 'bi-hourglass-split' : 'bi-download'}`}
              />
            </Button>
            {view('ADD_CONTRACTS') && (
              <Button
                variant='base'
                color='success'
                className='!mr-2'
                onClick={() => {
                  navigate('/admin/contract/add');
                }}
              >
                <i className='bi bi-plus-circle' />
              </Button>
            )}
            <Button variant='base' onClick={() => setOpen(true)}>
              <i className='bi bi-gear' />
            </Button>
          </div>
        </div>
        {/* {expanded && (
          <div className='my-shadow-2 rounded-lg px-4 w-full mt-2'>
            <ContractFiltersComponent />
          </div>
        )} */}
      </div>

      <ContractTotals search={search} refetchFlag={refetch} />

      <div className='component-table-wrapper h-[calc(100vh-235px)]'>
        <ContractTable
          emitRefetch={{ refetch, setRefetch }}
          dataPath='dictionary/contracts'
          emitTableColumns={{ open, setOpen }}
          tableName='contract'
          actionInfo={true}
        />
      </div>
      <ContractFilterDrawer open={expanded} setOpen={setExpanded} />

      {/* Hisobot tayyor — unikal havola orqali yuklab olish modali */}
      <Dialog
        open={!!exportReady}
        onClose={() => !downloading && setExportReady(null)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          <i className='bi bi-file-earmark-excel text-green-600 mr-2' />
          Hisobot tayyor
        </DialogTitle>
        <DialogContent>
          <div className='text-sm text-gray-600 break-all'>
            {exportReady?.filename}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            color='inherit'
            variant='contained'
            onClick={() => setExportReady(null)}
            disabled={downloading}
          >
            Yopish
          </Button>
          <Button
            color='success'
            variant='contained'
            onClick={handleDownloadReady}
            disabled={downloading}
            startIcon={
              downloading ? (
                <CircularProgress size={16} color='inherit' />
              ) : (
                <i className='bi bi-download' />
              )
            }
          >
            Yuklab olish
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default memo(Contract);
